import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, Clock, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";
import api from "@/lib/api";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useSEO(post ? {
    title: post.title,
    description: post.excerpt,
    image: post.image,
    type: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "image": post.image,
      "author": { "@type": "Organization", "name": "Keyz's Epic Adventure Tours" },
      "publisher": { "@type": "Organization", "name": "Keyz's Epic Adventure Tours" },
    }
  } : {});

  useEffect(() => {
    api.get(`/blog/${slug}`).then(r => { setPost(r.data); setLoading(false); }).catch(() => setLoading(false));
    api.get("/blog").then(r => { const d = Array.isArray(r.data) ? r.data : []; setRelatedPosts(d.filter(p => p.slug !== slug).slice(0, 2)); }).catch(() => {});
  }, [slug]);

  const sharePost = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied!")).catch(() => {});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone">
        <Navbar />
        <div className="pt-28 px-6 max-w-3xl mx-auto animate-pulse">
          <div className="h-6 bg-midnight/5 w-32 mb-4" />
          <div className="h-10 bg-midnight/5 w-3/4 mb-6" />
          <div className="h-64 bg-midnight/5 mb-8" />
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />

      <article className="pt-28 pb-24 px-6 max-w-3xl mx-auto" data-testid="blog-post-article">
        <Link to="/blog" className="inline-flex items-center gap-1 text-midnight/50 font-manrope text-sm mb-8 hover:text-sandy transition-colors duration-200" data-testid="back-to-blog">
          <ChevronLeft className="w-4 h-4" /> Back to Blog
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <Badge variant="outline" className="font-manrope text-[10px] uppercase tracking-wider border-sandy/30 text-sandy px-2 py-0">
            {post.category}
          </Badge>
          <span className="flex items-center gap-1 font-manrope text-[10px] text-midnight/30">
            <Clock className="w-2.5 h-2.5" /> {post.read_time}
          </span>
        </div>

        <h1 className="font-cormorant text-3xl sm:text-4xl lg:text-5xl font-light text-midnight leading-tight mb-6" data-testid="blog-post-title">
          {post.title}
        </h1>

        <p className="font-manrope text-base sm:text-lg text-midnight/50 mb-8 leading-relaxed">
          {post.excerpt}
        </p>

        <div className="overflow-hidden aspect-[16/9] mb-10">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        </div>

        {/* Article Content */}
        <div className="prose-custom" data-testid="blog-post-content">
          {post.content?.split('\n\n').map((para, i) => {
            if (para.startsWith('**') && para.endsWith('**')) {
              return <h2 key={i} className="font-cormorant text-2xl text-midnight mt-10 mb-4">{para.replace(/\*\*/g, '')}</h2>;
            }
            if (para.startsWith('**')) {
              const [heading, ...rest] = para.split('\n');
              return (
                <div key={i}>
                  <h3 className="font-cormorant text-xl text-midnight mt-8 mb-3">{heading.replace(/\*\*/g, '')}</h3>
                  {rest.map((line, j) => (
                    <p key={j} className="font-manrope text-sm text-midnight/60 leading-relaxed mb-3">
                      {line.replace(/\*\*/g, '')}
                    </p>
                  ))}
                </div>
              );
            }
            if (para.startsWith('- ')) {
              return (
                <ul key={i} className="list-disc list-inside space-y-1 mb-4 ml-4">
                  {para.split('\n').map((item, j) => (
                    <li key={j} className="font-manrope text-sm text-midnight/60">{item.replace(/^- /, '')}</li>
                  ))}
                </ul>
              );
            }
            return <p key={i} className="font-manrope text-sm sm:text-base text-midnight/60 leading-relaxed mb-4">{para}</p>;
          })}
        </div>

        {/* Share + CTA */}
        <div className="mt-12 pt-8 border-t border-midnight/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <button onClick={sharePost} className="flex items-center gap-2 text-midnight/40 hover:text-sandy transition-colors duration-200" data-testid="share-post-btn">
            <Share2 className="w-4 h-4" />
            <span className="font-manrope text-sm">Share this article</span>
          </button>
          <Link to="/tours" className="inline-flex items-center px-6 py-2.5 rounded-full bg-sandy text-midnight font-manrope text-sm hover:-translate-y-0.5 transition-transform duration-300" data-testid="blog-cta-btn">
            Browse Our Tours
          </Link>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-16" data-testid="related-posts">
            <h3 className="font-cormorant text-2xl font-light text-midnight mb-6">More from the Blog</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {relatedPosts.map(rp => (
                <Link key={rp.slug} to={`/blog/${rp.slug}`} className="group block" data-testid={`related-${rp.slug}`}>
                  <div className="overflow-hidden aspect-[16/10] mb-3">
                    <img src={rp.image} alt={rp.title} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" loading="lazy" />
                  </div>
                  <p className="font-manrope text-[10px] uppercase tracking-widest text-sandy mb-1">{rp.category}</p>
                  <h4 className="font-cormorant text-lg text-midnight leading-tight group-hover:text-sandy transition-colors duration-300">{rp.title}</h4>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      <Footer />
    </div>
  );
}
